<template>
  <div id="app" class="container-fluid">
    <full_cwc_head :visit_id="visit_id" />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading immunization records...</p>
      </div>
      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchRecords">Retry</button>
      </div>
      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-syringe"></i> Immunization</h3>
          <button class="btn-edit-header" @click="openAddModal">
            <i class="fas fa-plus"></i> Record Dose
          </button>
        </div>
        <div class="card-body">
          <div v-if="!records.length" class="no-data">No immunizations recorded yet.</div>
          <div v-else class="table-responsive">
            <table class="table-custom">
              <thead>
                <tr>
                  <th>Vaccine</th>
                  <th>Dose</th>
                  <th>Date Given</th>
                  <th>Next Due</th>
                  <th>Status</th>
                  <th>Batch #</th>
                  <th>Given By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in records" :key="record.id">
                  <td><strong>{{ record.vaccine }}</strong></td>
                  <td>{{ record.dose || "-" }}</td>
                  <td>{{ formatDate(record.date_given) }}</td>
                  <td>{{ formatDate(record.next_due_date) }}</td>
                  <td>
                    <span class="badge-outcome" :class="statusClass(record.status)">{{ record.status }}</span>
                  </td>
                  <td>{{ record.batch_number || "-" }}</td>
                  <td>{{ record.given_by || "-" }}</td>
                  <td>
                    <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-icon btn-icon-danger" title="Delete" @click="deleteRecord(record.id)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <ImmunizationModal
      v-if="showModal"
      :visitId="visit_id"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_cwc_head from "./full_cwc_head.vue";
import ImmunizationModal from "./ImmunizationModal.vue";
import { generalApiRequest } from "../../../../api/apiIndex";

export default {
  name: "CwcImmunizationView",
  components: { full_cwc_head, ImmunizationModal },
  data() {
    return {
      loading: false,
      error: null,
      visit_id: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.fetchRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const response = await generalApiRequest(`cwc_immunizations/visit/${this.visit_id}`, "GET", {}, 1);
        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load immunization records";
        }
      } catch (err) {
        console.error("Error fetching immunizations:", err);
        this.error = "An error occurred while loading immunization records. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },
    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },
    async handleSaved() {
      this.closeModal();
      await this.fetchRecords();
    },
    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this immunization record?")) return;
      try {
        const response = await generalApiRequest(`cwc_immunizations/${id}`, "DELETE", {}, 1);
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete immunization record");
        }
      } catch (error) {
        console.error("Error deleting immunization:", error);
        alert("Failed to delete immunization record. Please try again.");
      }
    },
    statusClass(status) {
      if (status === "given") return "outcome-live";
      if (status === "missed") return "outcome-missed";
      return "outcome-deferred";
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container-fluid { padding: 12px; background-color: #f5f7fa; min-height: 100vh; }
.main-content { margin-top: 12px; }
.loading-container, .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: white; border-radius: 10px; }
.loading-container i { font-size: 3rem; color: #c05621; margin-bottom: 1rem; }
.error-container i { font-size: 3rem; color: #dc3545; margin-bottom: 1rem; }
.btn-retry { background: #c05621; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.card { background: white; border-radius: 10px; border: 1px solid #e9ecef; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-bottom: 2px solid #dee2e6; }
.card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #495057; display: flex; align-items: center; gap: 8px; }
.card-header h3 i { color: #c05621; }
.btn-edit-header { background: linear-gradient(135deg, #ed8936 0%, #c05621 100%); color: white; border: none; padding: 7px 16px; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.card-body { padding: 16px 20px; }
.no-data { text-align: center; padding: 20px; color: #6c757d; font-size: 13px; font-style: italic; background: #f8f9fa; border-radius: 6px; }
.table-responsive { overflow-x: auto; }
.table-custom { width: 100%; border-collapse: collapse; font-size: 12px; }
.table-custom th { background: #f8f9fa; padding: 10px 8px; text-align: left; font-size: 11px; font-weight: 600; color: #6c757d; text-transform: uppercase; border-bottom: 2px solid #dee2e6; white-space: nowrap; }
.table-custom td { padding: 8px; border-bottom: 1px solid #e9ecef; color: #495057; white-space: nowrap; }
.table-custom tbody tr:hover { background: #f8f9fa; }
.badge-outcome { padding: 3px 10px; border-radius: 10px; font-size: 10px; font-weight: 600; text-transform: capitalize; }
.outcome-live { background: #d4edda; color: #155724; }
.outcome-missed { background: #f8d7da; color: #721c24; }
.outcome-deferred { background: #fff3cd; color: #856404; }
.btn-icon { background: none; border: none; color: #c05621; cursor: pointer; padding: 4px 6px; }
.btn-icon-danger { color: #dc3545; }
</style>
